import {
  Box,
  CircularProgress,
  Collapse,
  Paper,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material'
import IconButton from '@mui/material/IconButton'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import InventoryTable, { ColumnDef } from 'components/inventory/InventoryTable'
import React from 'react'
import { IStockCard, IStockCardPackaging } from 'interfaces/IStockCard'
import { Alert } from '@mui/material';
import { IPackagingParts } from 'interfaces/IPackaging'
import API from '@aws-amplify/api'
import { VALUE_IN, VALUE_OUT } from 'enums/stockCard'
import getSourceOrDestinationLabel from 'shared/getSourceOrDestinationLabel'



const headCells = [
  {
    id: 'packagingPartId',
    numeric: false,
    disablePadding: true,
    label: 'Kode Part Kemasan',
    width: 'medium',
  },
  {
    id: 'partName',
    numeric: false,
    disablePadding: false,
    label: 'Nama Part Kemasan',
    width: 'medium',
  },
  {
    id: 'stock',
    numeric: false,
    disablePadding: false,
    label: 'Stok',
    isStock: true,
    width: 'small',
  },
  // {
  //   id: '',
  //   numeric: false,
  //   disablePadding: true,
  //   label: '',
  //   width: 'small',
  //   isChevron: true,
  // },
]

// removed unused stockCardHeadCells helper

interface stockHistory {
  tbljoinStockCardPackaging: IStockCardPackaging
  tblStockCard: IStockCard
}

export default function StockCardPackagingHistory(props: {
  data: IPackagingParts[]
}) {
  const { data } = props
  const [packagingPartStockCardHistory, setPackagingPartStockCardHistory] =
    React.useState<stockHistory[]>([])
  const [isLoading, setIsLoading] = React.useState(false)
  const [expandedRows, setExpandedRows] = React.useState<
    Record<string, boolean>
  >(() =>
    data.reduce<Record<string, boolean>>((acc, item) => {
      acc[item.packagingPartId] = false
      return acc
    }, {})
  )

  React.useEffect(() => {
    setExpandedRows(
      data.reduce<Record<string, boolean>>((acc, item) => {
        acc[item.packagingPartId] = false
        return acc
      }, {})
    )
  }, [data])

  const loadHistory = async (id: string) => {
    setIsLoading(true)
    try {
      const stockCards: stockHistory[] = await API.get(
        'products',
        `/premix/stockCardPackagingPart/${id}`,
        {}
      )
      setPackagingPartStockCardHistory(stockCards)
    } finally {
      setIsLoading(false)
    }
  }

  const toggleOpenStatus = async (id: string) => {
    const willExpand = !expandedRows[id]
    const next = data.reduce<Record<string, boolean>>((acc, packagingPart) => {
      acc[packagingPart.packagingPartId] =
        packagingPart.packagingPartId === id ? willExpand : false
      return acc
    }, {})
    setExpandedRows(next)
    if (willExpand) {
      setPackagingPartStockCardHistory([])
      await loadHistory(id)
    }
  }

  const pagedRows = data

  const createHistoryColumns = (
    direction: typeof VALUE_IN | typeof VALUE_OUT
  ): ColumnDef<stockHistory>[] => [
      {
        id: `${direction}-code`,
        label: 'Kode Kartu Stok',
        render: (row) => row.tblStockCard.stockCardDisplayId ?? '-',
      },
      {
        id: `${direction}-date`,
        label: 'Tgl',
        render: (row) =>
          row.tblStockCard.createdAt
            ? new Date(row.tblStockCard.createdAt).toLocaleDateString()
            : '-',
      },
      {
        id: `${direction}-confirmed-qty`,
        label: 'Qty Dikonfirmasi',
        align: 'right',
        render: (row) => row.tbljoinStockCardPackaging.confirmedQuantity ?? 0,
      },
      {
        id: `${direction}-destination`,
        label: direction === VALUE_IN ? 'Dari' : 'Tujuan',
        render: (row) =>
          getSourceOrDestinationLabel(row.tblStockCard.outgoingDestination),
      },
      {
        id: `${direction}-confirmed-by`,
        label: direction === VALUE_IN ? 'Dimasukin Oleh' : 'Dikeluarkan Oleh',
        render: (row) => row.tbljoinStockCardPackaging.confirmedBy ?? '-',
      },
      {
        id: `${direction}-notes`,
        label: 'Catatan',
        render: (row) => row.tblStockCard.notes ?? '-',
      },
    ]

  return (
    <Box>
      {data.length === 0 && (
        <Alert severity="warning">Part Kemasan Tidak Ditemukan</Alert>
      )}
      {data.length !== 0 && (
        <Paper>
          <Box p={2} pt={0}>

            {(() => {
              const mapped: ColumnDef<IPackagingParts>[] = headCells
                .filter((hc) => hc.width !== 'hidden')
                .map((hc) => ({
                  id: hc.id,
                  label: hc.label || '',
                  align: hc.numeric ? 'right' : 'left',
                  render: (row) => row[hc.id as keyof IPackagingParts] ?? '-',
                }))

              const columns: ColumnDef<IPackagingParts>[] = [
                {
                  id: '__expander',
                  label: '',
                  render: (row) => (
                    <IconButton
                      aria-label="expand row"
                      size="small"
                      onClick={(e: React.MouseEvent) => {
                        e.stopPropagation()
                        toggleOpenStatus(row.packagingPartId)
                      }}
                    >
                      {expandedRows[row.packagingPartId] ? (
                        <KeyboardArrowUpIcon />
                      ) : (
                        <KeyboardArrowDownIcon />
                      )}
                    </IconButton>
                  ),
                },
                ...mapped,
              ]

              const columnCount = columns.length

              const rowExtra = (row: IPackagingParts) => {
                const isOpen = expandedRows[row.packagingPartId]
                if (!isOpen) {
                  return null
                }

                // Sort all history descending (newest first)
                const sortedHistory = [...packagingPartStockCardHistory].sort(
                  (a, b) =>
                    new Date(b.tblStockCard.createdAt ?? 0).getTime() -
                    new Date(a.tblStockCard.createdAt ?? 0).getTime()
                )

                const inboundRows = sortedHistory.filter(
                  (h) => h.tblStockCard.inOutDirection === VALUE_IN
                )
                const outboundRows = sortedHistory.filter(
                  (h) => h.tblStockCard.inOutDirection === VALUE_OUT
                )

                const inboundColumns = createHistoryColumns(VALUE_IN)
                const outboundColumns = createHistoryColumns(VALUE_OUT)
                const hasHistory = inboundRows.length || outboundRows.length

                return (
                  <TableRow>
                    <TableCell colSpan={columnCount} style={{ paddingTop: 0 }}>
                      <Collapse in={isOpen} timeout="auto" unmountOnExit>
                        <Box mt={2}>
                          <Typography variant="subtitle1" gutterBottom>
                            {row.packagingPartId} — {row.partName}
                          </Typography>
                          {isLoading ? (
                            <Box p={2}>
                              <CircularProgress size={24} />
                            </Box>
                          ) : !hasHistory ? (
                            <Typography variant="body2">
                              Belum ada sejarah.
                            </Typography>
                          ) : (
                            <Box display="flex" flexWrap="wrap" gap={16}>
                              <Box flex={1} minWidth={280}>
                                <Typography
                                  variant="subtitle2"
                                  align="left"
                                  gutterBottom
                                >
                                  IN
                                </Typography>
                                <InventoryTable
                                  columns={inboundColumns}
                                  rows={inboundRows}
                                  getRowKey={(historyRow) =>
                                    `${historyRow.tblStockCard.createdAt}-IN-${historyRow.tbljoinStockCardPackaging.confirmedBy}`
                                  }
                                  emptyMessage="Tidak ada data masuk"
                                />
                              </Box>
                              <Box flex={1} minWidth={280}>
                                <Typography
                                  variant="subtitle2"
                                  align="left"
                                  gutterBottom
                                >
                                  OUT
                                </Typography>
                                <InventoryTable
                                  columns={outboundColumns}
                                  rows={outboundRows}
                                  getRowKey={(historyRow) =>
                                    `${historyRow.tblStockCard.createdAt}-OUT-${historyRow.tbljoinStockCardPackaging.confirmedBy}`
                                  }
                                  emptyMessage="Tidak ada data keluar"
                                />
                              </Box>
                            </Box>
                          )}
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                );
              }

              return (
                <InventoryTable
                  columns={columns}
                  rows={pagedRows}
                  getRowKey={(r) => r.packagingPartId}
                  rowExtra={rowExtra}
                  rowStyle={(r) =>
                    expandedRows[r.packagingPartId]
                      ? { backgroundColor: '#e3f2fd' }
                      : undefined
                  }
                />
              )
            })()}
          </Box>
        </Paper>
      )}
    </Box>
  );
}
